import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { Logo } from "@/components/Logo";
import { AppFooter } from "@/components/AppFooter";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { PasswordInput } from "@/components/PasswordInput";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";
import { ShieldCheck, ArrowLeft } from "lucide-react";

export default function AdminLogin() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const { login } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    const res = await login(email, password);
    setSubmitting(false);
    if (!res.ok) {
      toast.error(res.error);
      return;
    }
    if (res.user.role !== "super_admin") {
      toast.error("This portal is for super admins only.");
      return;
    }
    toast.success(`Welcome back, ${res.user.name}`);
    navigate("/admin", { replace: true });
  };

  return (
    <div className="min-h-screen flex flex-col bg-background" data-testid="admin-login-page">
      <main className="flex-1 flex items-center justify-center px-6 py-12">
        <div className="max-w-md w-full">
          <Logo size="lg" />
          <div className="mt-10 flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-primary font-semibold">
            <ShieldCheck size={14} /> Super admin portal
          </div>
          <h1 className="text-4xl font-bold tracking-tight mt-2">Sign in</h1>
          <p className="text-muted-foreground mt-2">Manage dietitians, branding & system settings.</p>

          <form onSubmit={handleSubmit} className="mt-8 space-y-5" data-testid="admin-login-form">
            <div className="space-y-2">
              <Label htmlFor="email">Email</Label>
              <Input id="email" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} placeholder="super@example.com" data-testid="login-email-input" className="h-12" />
            </div>
            <div className="space-y-2">
              <Label htmlFor="password">Password</Label>
              <PasswordInput id="password" required value={password} onChange={(e) => setPassword(e.target.value)} placeholder="••••••••" testId="login-password-input" className="h-12" />
            </div>
            <Button type="submit" disabled={submitting} className="w-full h-12 text-base font-semibold rounded-full" data-testid="login-submit-button">
              {submitting ? "Signing in…" : "Sign in"}
            </Button>
          </form>

          <div className="mt-8 flex items-center gap-5 text-sm">
            <Link to="/dietitian/login" className="text-muted-foreground hover:text-foreground inline-flex items-center gap-1.5" data-testid="link-dietitian-login">
              <ArrowLeft size={14} /> Dietitian login
            </Link>
            <Link to="/login" className="text-muted-foreground hover:text-foreground" data-testid="link-patient-login">
              Patient login
            </Link>
          </div>
        </div>
      </main>
      <AppFooter />
    </div>
  );
}
